import { useState } from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { useAuth } from "@/contexts/auth"
import { UserRole } from "@/types/enums"
import { CompanySettings } from "@/components/settings/CompanySettings"
import { ShiftSettings } from "@/components/settings/ShiftSettings"
import { DepartmentSettings } from "@/components/settings/departments/DepartmentSettings"
import { HolidaySettings } from "@/components/settings/holidays/HolidaySettings"
import { OvertimeReasonsSettings } from "@/components/settings/overtime-reasons"
import UserManagementPage from "@/pages/UserManagement"

const Settings = () => {
  const [activeTab, setActiveTab] = useState("organization")
  const { user } = useAuth()
  const canEditSettings = user?.role === UserRole.HR || user?.role === UserRole.SUPER_ADMIN
  
  if (!canEditSettings) {
    return (
      <div className="container mx-auto p-6">
        <div className="space-y-6">
          <div className="text-center">
            <h1 className="text-3xl font-bold tracking-tight mb-2">System Settings</h1>
            <p className="text-muted-foreground text-lg">
              Configure application settings and preferences
            </p>
          </div>
          
          <Alert className="max-w-2xl mx-auto">
            <AlertDescription>
              Only HR and Administrators can access and modify system settings.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }
  
  return (
    <div className="container mx-auto p-6">
      {/* Tabs */}
      <Tabs 
        defaultValue="organization" 
        value={activeTab} 
        onValueChange={setActiveTab}
        className="w-full"
      >
        <TabsList className="grid w-full max-w-4xl grid-cols-5 bg-background border border-border shadow-sm gap-1 p-1">
          <TabsTrigger 
            value="organization" 
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm transition-all duration-200 text-sm px-3 py-2"
          >
            Organization
          </TabsTrigger>
          <TabsTrigger 
            value="departments" 
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm transition-all duration-200 text-sm px-3 py-2"
          >
            Departments
          </TabsTrigger>
          <TabsTrigger 
            value="holidays" 
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm transition-all duration-200 text-sm px-3 py-2"
          >
            Holidays
          </TabsTrigger>
          <TabsTrigger 
            value="ot-reasons" 
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm transition-all duration-200 text-sm px-3 py-2"
          >
            OT Reasons
          </TabsTrigger>
          <TabsTrigger 
            value="access-control" 
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm transition-all duration-200 text-sm px-3 py-2"
          >
            Access Control
          </TabsTrigger>
        </TabsList>
        
        {/* Tab Content */}
        <div className="mt-6">
          <TabsContent value="organization" className="space-y-6 mt-0">
            <CompanySettings />
            <ShiftSettings />
          </TabsContent>
          
          <TabsContent value="departments" className="space-y-6 mt-0">
            <DepartmentSettings />
          </TabsContent>

          <TabsContent value="holidays" className="space-y-6 mt-0">
            <HolidaySettings />
          </TabsContent>

          <TabsContent value="ot-reasons" className="space-y-6 mt-0">
            <OvertimeReasonsSettings />
          </TabsContent>

          <TabsContent value="access-control" className="mt-0">
            <UserManagementPage />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  )
}

export default Settings
